import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import {
  CreateRecipe,
  PaginatedRecipesResponse,
  PaginationMeta,
  Recipe,
  RecipeListRequest,
} from '../types/recipe'
import {
  loadRecipeList,
  loadRecipes,
  modifyRecipe,
  removeRecipe,
  saveRecipe,
} from '../api/recipe'

const emptyPagination: PaginationMeta = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 0,
}

// get all recipes
export const getRecipes = createAsyncThunk(
  'recipe/getRecipes',
  async (_, thunkApi) => {
    try {
      return await loadRecipes()
    } catch (e) {
      return thunkApi.rejectWithValue('getRecipeError')
    }
  },
)

export const getRecipeList = createAsyncThunk(
  'recipe/getRecipeList',
  async (request: RecipeListRequest, thunkApi) => {
    try {
      return await loadRecipeList(request)
    } catch (e) {
      return thunkApi.rejectWithValue('getRecipeListError')
    }
  },
)

// add recipe
export const addRecipe = createAsyncThunk(
  'recipe/addRecipe',
  async (recipe: CreateRecipe, thunkApi) => {
    try {
      return await saveRecipe(recipe)
    } catch (e) {
      return thunkApi.rejectWithValue('addRecipeError')
    }
  },
)

// update recipe
export const updateRecipe = createAsyncThunk(
  'recipe/updateRecipe',
  async (recipe: Recipe, thunkApi) => {
    try {
      return await modifyRecipe(recipe)
    } catch (e) {
      return thunkApi.rejectWithValue('updateRecipeError')
    }
  },
)

// delete objective
export const deleteRecipe = createAsyncThunk(
  'recipe/deleteRecipe',
  async (id: string, thunkApi) => {
    try {
      return await removeRecipe(id)
    } catch (e) {
      return thunkApi.rejectWithValue('deleteRecipeError')
    }
  },
)

interface RecipeSliceState {
  recipes: Recipe[]
  pagedRecipes: Recipe[]
  loading: boolean
  error?: SerializedError
  pagination: PaginationMeta
  activeListRequestId?: string
}

const initialState: RecipeSliceState = {
  recipes: [],
  pagedRecipes: [],
  loading: false,
  error: undefined,
  pagination: emptyPagination,
  activeListRequestId: undefined,
}

const recipeSlice = createSlice({
  name: 'recipe',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getRecipes.fulfilled, (state, action: PayloadAction<Recipe[]>) => {
      state.recipes = action.payload
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getRecipes.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getRecipes.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(getRecipeList.fulfilled, (
      state,
      action: PayloadAction<PaginatedRecipesResponse> & { meta: any },
    ) => {
      if (state.activeListRequestId !== action.meta.requestId) {
        return
      }

      state.pagedRecipes = action.payload.items
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      }
      state.activeListRequestId = undefined
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getRecipeList.pending, (state, action: any) => {
      state.activeListRequestId = action.meta.requestId
      state.pagedRecipes = []
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getRecipeList.rejected, (state, action: any) => {
      if (state.activeListRequestId !== action.meta.requestId) {
        return
      }

      state.activeListRequestId = undefined
      state.loading = false
      state.error = action.error
    })
    builder.addCase(addRecipe.fulfilled, (state, action: PayloadAction<Recipe>) => {
      state.recipes = [action.payload, ...state.recipes]

      if (state.pagination.page === 1) {
        state.pagedRecipes = [action.payload, ...state.pagedRecipes].slice(
          0,
          state.pagination.limit,
        )
        state.pagination.total += 1
        state.pagination.totalPages = Math.ceil(
          state.pagination.total / state.pagination.limit,
        )
      }

      state.loading = false
      state.error = undefined
    })
    builder.addCase(addRecipe.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(addRecipe.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(deleteRecipe.fulfilled, (state, action: PayloadAction<string>) => {
      state.recipes = state.recipes.filter(
        (recipe) => recipe.id !== action.payload,
      )
      const wasOnCurrentPage = state.pagedRecipes.some(
        (recipe) => recipe.id === action.payload,
      )

      state.pagedRecipes = state.pagedRecipes.filter(
        (recipe) => recipe.id !== action.payload,
      )

      if (wasOnCurrentPage && state.pagination.total > 0) {
        state.pagination.total -= 1
        state.pagination.totalPages =
          state.pagination.total === 0
            ? 0
            : Math.ceil(state.pagination.total / state.pagination.limit)
      }

      state.loading = false
      state.error = undefined
    })
    builder.addCase(deleteRecipe.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(deleteRecipe.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(updateRecipe.fulfilled, (state, action: PayloadAction<Recipe>) => {
      if (state.recipes.length > 0) {
        const updatedObjectives = state.recipes.map((recipe) =>
          recipe.id === action.payload.id ? action.payload : recipe,
        )
        state.recipes = updatedObjectives
      }

      state.pagedRecipes = state.pagedRecipes.map((recipe) =>
        recipe.id === action.payload.id ? action.payload : recipe,
      )

      state.loading = false
      state.error = undefined
    })
    builder.addCase(updateRecipe.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(updateRecipe.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
  },

  // (builder) => {
  //     builder.addCase(getDevelopers.fulfilled, (state, action) => {
  //         state.developers = action.payload as IDeveloper[];
  //     })

  //     builder.addCase(addDeveloper.fulfilled, (state, action) => {
  //         state.developers.push(action.payload as IDeveloper);
  //     })

  //     builder.addCase(deleteDeveloper.fulfilled, (state, action) => {
  //         state.developers = state.developers.filter(developer => developer.id != action.payload)
  //     })
  // }
})

export const {} = recipeSlice.actions
export default recipeSlice.reducer
